import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useRevenuePulse } from "@/hooks/useRevenuePulse";
import { useArenaFeed } from "@/hooks/useArenaFeed";
import { useProposalOpens } from "@/hooks/useProposalOpens";
import AssistantWidget from "@/components/AssistantWidget";
import { ArenaTakeover } from "@/components/ArenaTakeover";
import { AudioArmBanner } from "@/components/AudioArmBanner";
import { List, BookOpen, Settings as SettingsIcon, Users as UsersIcon, LogOut, Activity, LayoutDashboard, Wallet, Crosshair } from "lucide-react";

function NavItem({ to, icon: Icon, label, testId }) {
  return (
    <NavLink
      to={to}
      end
      data-testid={testId}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-2 text-sm border-l-2 transition-colors ${
          isActive
            ? "text-ink border-cyan bg-bg-hover"
            : "text-ink-secondary border-transparent hover:text-ink hover:bg-bg-hover"
        }`
      }
    >
      <Icon size={16} className="shrink-0" />
      <span>{label}</span>
    </NavLink>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Live scoreboard: fires a toast + chime whenever a new subscription lands.
  // Self-guards to admin-only inside the hook.
  useRevenuePulse();
  // Arena team-wide close broadcasts (walk-up + takeover)
  useArenaFeed();
  // Real-time proposal-open alerts (rep + OM + admin)
  useProposalOpens();

  const isAdmin = user?.role === "admin";

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="flex h-screen w-screen bg-bg text-ink overflow-hidden">
      {/* Sidebar */}
      <aside
        className="w-60 shrink-0 border-r border-line bg-bg-surface flex flex-col"
        data-testid="app-sidebar"
      >
        <div className="px-4 py-5 border-b border-line">
          <div className="flex items-baseline gap-2">
            <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-cyan">
              Main St.
            </div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">
              v1
            </div>
          </div>
          <div className="mt-1 text-[15px] font-semibold text-ink tracking-tight">
            Sales Console
          </div>
        </div>

        <nav className="py-3 flex-1 overflow-y-auto">
          <div className="px-4 pb-2 text-[10px] font-mono uppercase tracking-widest text-ink-muted">
            Cockpit
          </div>
          <NavItem to="/dashboard" icon={LayoutDashboard} label="Dashboard" testId="nav-dashboard" />
          <NavItem to="/leads" icon={List} label="Leads Board" testId="nav-leads" />
          <NavItem to="/arena" icon={Crosshair} label="Arena" testId="nav-arena" />
          <NavItem to="/playbook" icon={BookOpen} label="Playbook" testId="nav-playbook" />
          <NavItem to="/commissions" icon={Wallet} label="Commissions" testId="nav-commissions" />

          {isAdmin && (
            <>
              <div className="mt-4 px-4 pb-2 text-[10px] font-mono uppercase tracking-widest text-ink-muted">
                Admin
              </div>
              <NavItem to="/users" icon={UsersIcon} label="Team" testId="nav-users" />
              <NavItem to="/settings" icon={SettingsIcon} label="Settings" testId="nav-settings" />
            </>
          )}
        </nav>

        <div className="border-t border-line p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="min-w-0 flex-1">
              <div
                className="truncate text-sm text-ink font-medium"
                data-testid="current-user-name"
              >
                {user?.name}
              </div>
              <div className="flex items-center gap-2 text-[11px] text-ink-muted font-mono">
                <span
                  className="inline-block h-1.5 w-1.5 rounded-full"
                  style={{ backgroundColor: "#22D3C0" }}
                />
                <span className="uppercase tracking-wider">{user?.role}</span>
              </div>
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-ink-secondary hover:text-ink hover:bg-bg-hover border border-line rounded-sm transition-colors"
          >
            <LogOut size={13} />
            Sign out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-hidden flex flex-col">{children}</main>

      {/* Global AI helper */}
      <AssistantWidget />
      {/* Arena — full-screen close celebration (queued) */}
      <ArenaTakeover />
      {/* One-time click-to-arm audio banner so walk-up tracks play flawlessly */}
      <AudioArmBanner />
    </div>
  );
}
